import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useSaveFlow } from '../../components/confirm';
import { LoaderSymbol, BusyIcon } from '../../components/loading';
import type { CrudField, ModuleCrudConfig } from './moduleCrud';

interface Option { id: string; name: string }

interface Props {
  config: ModuleCrudConfig;
  /** null = create (New); a record id = open that record. */
  id: string | null;
  /** How the list opened the record. 'view' = READ-ONLY (a row click, matching
   *  the legacy XAF flow: row → DetailView to look at the data, with an Edit
   *  button to switch). 'edit' = editable straight away (the Edit pencil /
   *  toolbar Edit). Omitted falls back to view — or edit when the form sets
   *  `detailOpenInEdit` — matching <SettingsDetailPage>. Ignored for New, which
   *  is always editable. */
  mode?: 'view' | 'edit';
  onClose: () => void;
  onSaved: () => void;
}

/** Generic create/edit modal driven by a ModuleCrudConfig.  Reuses the shared
 *  ".qs-*" dialog chrome so it looks identical to the Query Samples modal.
 *
 *  Opt IN per form with `MODULE_CRUD[key].dialogEdit = true`; without that flag a
 *  form uses the full-page <SettingsDetailPage> (standing rule 12), which remains
 *  the default everywhere. Currently used by Settings ▸ Permit Library New ▸
 *  Permit Type New, whose approved design is this compact modal. */
export function ModuleCrudDialog({ config, id, mode, onClose, onSaved }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const editing = id !== null;
  // A NEW record is always editable; an existing one starts in whatever mode the
  // list opened it with, and the Edit button promotes view → edit in place.
  // An UNSPECIFIED mode falls back to view (or edit when the form opts in with
  // detailOpenInEdit) — the SAME rule <SettingsDetailPage> uses, so the popup and
  // the full-page detail never disagree about what a given open means.
  const initialEditable = !editing
    || (mode ?? (config.detailOpenInEdit ? 'edit' : 'view')) === 'edit';
  const [editable, setEditable] = useState(initialEditable);
  useEffect(() => { setEditable(initialEditable); }, [initialEditable, id]);
  const readOnly = !editable;
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [options, setOptions] = useState<Record<string, Option[]>>({});
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (k: string, v: unknown) => setValues((s) => ({ ...s, [k]: v }));

  // Load select options once (per dialog open).
  useEffect(() => {
    let alive = true;
    const selects = config.fields.filter((f) => f.type === 'select' && f.optionsUrl);
    Promise.all(
      selects.map(async (f) => {
        try {
          const { data } = await api.get<{ data?: Option[] }>(f.optionsUrl!);
          return [f.key, data.data ?? []] as const;
        } catch {
          return [f.key, [] as Option[]] as const;
        }
      }),
    ).then((pairs) => {
      if (alive) setOptions(Object.fromEntries(pairs));
    });
    return () => { alive = false; };
  }, [config]);

  // Fields shown for THIS mode. `editOnly` fields are hidden on New (e.g.
  // Active, which the DB defaults on create) — hidden everywhere that matters:
  // render, defaults, the required check, and the save payload.
  const visibleFields = useMemo(
    () => config.fields.filter((f) => editing || !f.editOnly),
    [config.fields, editing],
  );

  // New: seed from each field's defaultValue (e.g. Active ticked by default),
  // matching what <SettingsDetailPage> does for the full-page detail. Without
  // this a new record starts with every checkbox cleared.
  useEffect(() => {
    if (editing) return;
    const seed: Record<string, unknown> = {};
    for (const f of visibleFields) {
      if (f.defaultValue !== undefined) seed[f.key] = f.defaultValue;
    }
    setValues(seed);
  }, [visibleFields, editing]);

  // Edit: prefill from GET base/{id}.
  useEffect(() => {
    if (!editing) return;
    let alive = true;
    setLoading(true); setError(null);
    api.get<{ data?: Record<string, unknown> }>(`${config.basePath}/${id}`)
      .then((res) => {
        if (!alive) return;
        const d = res.data.data ?? {};
        const next: Record<string, unknown> = {};
        for (const f of config.fields) {
          const raw = d[f.key];
          if (f.type === 'date') next[f.key] = toDateInput(raw);
          else if (f.type === 'checkbox') next[f.key] = Boolean(raw);
          else if (f.type === 'select') next[f.key] = raw == null ? '' : String(raw);
          else next[f.key] = raw == null ? '' : String(raw);
        }
        setValues(next);
      })
      .catch(() => { if (alive) setError(`Could not load this ${config.entityLabel.toLowerCase()}.`); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [config, id, editing]);

  const missingRequired = useMemo(
    () => visibleFields.filter(
      (f) => f.required && f.type !== 'readonly'
        && String(values[f.key] ?? '').trim() === '',
    ).map((f) => f.label),
    [visibleFields, values],
  );

  const save = async () => {
    if (missingRequired.length > 0) {
      setError(`Required: ${missingRequired.join(', ')}.`);
      return;
    }
    setSaving(true); setError(null);
    const payload: Record<string, unknown> = {};
    for (const f of visibleFields) {
      if (f.type === 'readonly') continue;
      const v = values[f.key];
      if (f.type === 'checkbox') payload[f.key] = Boolean(v);
      else {
        const s = v == null ? '' : String(v).trim();
        payload[f.key] = s === '' ? null : s;
      }
    }
    try {
      // Shared ALPACA-style save flow: "Do you want to Save?" → save via the
      // existing api.put/post → "Saved successfully." → close (onSaved).
      await runSave({
        save: async () => {
          if (editing) await api.put(`${config.basePath}/${id}`, payload);
          else await api.post(config.basePath, payload);
        },
        successMessage: `${config.entityLabel} saved successfully.`,
        onSaved: () => onSaved(),
        onError: (e) => {
          const msg = (e as { response?: { data?: { error?: { message?: string } } } })
            .response?.data?.error?.message ?? 'Save failed.';
          notify.error(msg);
        },
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="qs-overlay" onMouseDown={onClose}>
      <div className="qs-dialog" onMouseDown={(e) => e.stopPropagation()} style={{ width: 560, maxWidth: '95vw' }}>
        <div className="qs-titlebar">
          <span>
            {!editing ? `New ${config.entityLabel}`
              : readOnly ? config.entityLabel
              : `Edit ${config.entityLabel}`}
          </span>
        </div>

        <div className="qs-body" style={{ maxHeight: '70vh', overflow: 'auto' }}>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#666' }}>
              <LoaderSymbol size={18} /> Loading…
            </div>
          ) : (
            <div className="qs-grid-wide mcd-fields" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {visibleFields.map((f) => (
                <Field key={f.key} field={f} value={values[f.key]} readOnly={readOnly}
                  options={options[f.key] ?? []} onChange={(v) => set(f.key, v)} />
              ))}
              {error && (
                <div style={{ color: '#b71c1c', fontSize: 12, padding: '4px 2px' }}>
                  <i className="ti ti-alert-triangle" /> {error}
                </div>
              )}
            </div>
          )}
        </div>

        {/* VIEW mode — Close + Edit (promotes to editable without reopening).
            EDIT / NEW — Cancel → Save, right-aligned, Save solid: the approved
            prototype's button order ("Save" on both New and Edit, never
            "Create"). */}
        <div className="qs-footer qs-footer-end">
          {readOnly ? (
            <>
              <button className="ca-btn" onClick={onClose}>Close</button>
              <button className="ca-btn ca-btn-solid" onClick={() => setEditable(true)}
                disabled={loading}>
                <span className="ti ti-pencil" /> Edit
              </button>
            </>
          ) : (
            <>
              <button className="ca-btn" onClick={onClose} disabled={saving}>Cancel</button>
              <button className="ca-btn ca-btn-solid" onClick={save} disabled={saving || loading}>
                <BusyIcon busy={saving} icon="ti-device-floppy" /> Save
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ field, value, options, readOnly = false, onChange }: {
  field: CrudField; value: unknown; options: Option[]; readOnly?: boolean;
  onChange: (v: unknown) => void;
}) {
  const id = `crud-${field.key}`;
  // `disabled` (not just readOnly) so selects and checkboxes are inert too, and
  // the browser gives every editor the same greyed affordance in view mode.
  const common = { id, className: 'qs-input', disabled: readOnly, style: { width: '100%' } as const };

  return (
    <div className="qs-field">
      {/* Required marker uses the SHARED `.req-star` (theme/boStandard.css,
          var(--ai-red)) — the same red asterisk the grid headers and the
          full-page detail use, so "required" reads identically everywhere
          (BO UI Standard §4). */}
      <label className="qs-label" htmlFor={id}>
        {field.label}
        {field.required && <span className="req-star"> *</span>}
      </label>
      {field.type === 'readonly' ? (
        <input {...common} type="text" readOnly disabled
          value={value == null || value === '' ? '(auto)' : String(value)} />
      ) : field.type === 'checkbox' ? (
        // Bare checkbox — the caption already sits in the left .qs-label column,
        // so repeating it beside the box would print the label twice.
        <input id={id} type="checkbox" checked={Boolean(value)} disabled={readOnly}
          onChange={(e) => onChange(e.target.checked)} />
      ) : field.type === 'select' ? (
        <select {...common} value={value == null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}>
          <option value="">{field.noneLabel ?? '— None —'}</option>
          {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      ) : field.type === 'date' ? (
        <input {...common} type="date" value={value == null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)} />
      ) : field.type === 'textarea' ? (
        <textarea {...common} rows={3} maxLength={field.maxLength}
          value={value == null ? '' : String(value)} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input {...common} type="text" maxLength={field.maxLength}
          value={value == null ? '' : String(value)} onChange={(e) => onChange(e.target.value)} />
      )}
      {field.hint && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{field.hint}</div>}
    </div>
  );
}

/** ISO / date string → yyyy-MM-dd for <input type=date>. */
function toDateInput(raw: unknown): string {
  if (raw == null || raw === '') return '';
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}
